import Box from "@mui/material/Box"
import Typography from "@mui/material/Typography"
import { FieldArray, FormikProps, FormikProvider } from "formik"
import { useEffect, useRef } from "react"

import { faCircleExclamation } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import FileUploaderV2 from "components/FileUploaderV2"
import InputTag from "components/Input/InputTag"
import MuiAutoComplete from "components/MuiAutocomplete"
import MuiCheckbox from "components/MuiCheckbox"
import MuiTextField from "components/MuiTextField"
import RadioGroup from "components/RadioGroup/RadioGroup"
import { useGetPaymentConditionsQuery } from "pages/Finance/FinanceServices"
import { useUploadPaymentEvidenceMutation } from "pages/Projects/ProjectServices"
import { PAYMENT_TERMS_INITIAL_VALUES, PaymentDetailsFormValues } from "./usePaymentDetails"

export enum CONDITIONS {
    ALREADY_PAID_AS_ADVANCE = "Already paid as advance",
    OTHER_CONDITION = "Other conditions",
}

export enum PAYMENT_TYPE {
    SET_CONDITIONAL_TIME_FRAME = "Set conditional time frame",
    SET_CUSTOM_DATE = "Set custom date",
}

enum PAYMENT_ALLOCATION_TYPE {
    PERCENT = "percent",
    AMOUNT = "amount",
}

const PAYMENT_TYPE_OPTIONS = [
    { id: 0, title: PAYMENT_TYPE.SET_CUSTOM_DATE },
    { id: 1, title: PAYMENT_TYPE.SET_CONDITIONAL_TIME_FRAME },
]

const PAYMENT_ALLOCATION_OPTIONS = [
    { id: PAYMENT_ALLOCATION_TYPE.PERCENT, title: "%" },
    { id: PAYMENT_ALLOCATION_TYPE.AMOUNT, title: "Amount" },
]

const TIME_UOM_OPTIONS = [
    { id: 1, title: "Days" },
    { id: 2, title: "Weeks" },
    { id: 3, title: "Months" },
]

const getNumericValue = (value: any) => {
    const parsedValue = parseFloat(String(value ?? 0))

    return Number.isFinite(parsedValue) ? parsedValue : 0
}

const isPaymentTermsTotalValid = (paymentTermsObj: any[], totalAmountValue: any) => {
    if (!paymentTermsObj?.length) {
        return true
    }

    const allPercent = paymentTermsObj.every(
        (item: any) =>
            (item?.allocation_type?.id || PAYMENT_ALLOCATION_TYPE.PERCENT) === PAYMENT_ALLOCATION_TYPE.PERCENT
    )

    if (allPercent) {
        const totalPercent = paymentTermsObj.reduce(
            (acc: number, obj: any) => acc + getNumericValue(obj?.pmt_percent),
            0
        )

        return totalPercent === 100
    }

    const totalAmount = getNumericValue(totalAmountValue)

    if (!Number.isFinite(totalAmount) || totalAmount <= 0) {
        return false
    }

    const fixedAmountTotal = paymentTermsObj.reduce(
        (acc: number, obj: any) =>
            acc +
            ((obj?.allocation_type?.id || PAYMENT_ALLOCATION_TYPE.PERCENT) === PAYMENT_ALLOCATION_TYPE.AMOUNT
                ? getNumericValue(obj?.pmt_amount)
                : 0),
        0
    )
    const percentTotal = paymentTermsObj.reduce(
        (acc: number, obj: any) =>
            acc +
            ((obj?.allocation_type?.id || PAYMENT_ALLOCATION_TYPE.PERCENT) === PAYMENT_ALLOCATION_TYPE.PERCENT
                ? getNumericValue(obj?.pmt_percent)
                : 0),
        0
    )
    const allocatedTotal = fixedAmountTotal + (totalAmount * percentTotal) / 100

    return Math.abs(allocatedTotal - totalAmount) < 0.01
}

interface PaymentTermProps {
    count: number
    id: number
    data: any
    removeHandler: () => void
    formik: FormikProps<PaymentDetailsFormValues>
    financeTicketId?: number | null
    showEvidenceUpload?: boolean
    /** false locks the allocation to %, for callers whose terms are percentage shares only. */
    allowAmountAllocation?: boolean
}

const PaymentTerm = ({
    count,
    removeHandler,
    id,
    data,
    formik,
    financeTicketId,
    showEvidenceUpload,
    allowAmountAllocation = true,
}: PaymentTermProps) => {
    const showError = formik.submitCount > 0
    const [uploadPaymentEvidence] = useUploadPaymentEvidenceMutation()

    const { data: paymentConditions } = useGetPaymentConditionsQuery({
        ticket_id: financeTicketId ? String(financeTicketId) : "",
    })

    const conditionOptions = paymentConditions?.map(item => ({ id: item.id, title: item.name })) || []
    const otherConditionId = conditionOptions.find(option => option.title === CONDITIONS.OTHER_CONDITION)?.id
    const alreadyPaidAsAdvanceId = conditionOptions.find(
        option => option.title === CONDITIONS.ALREADY_PAID_AS_ADVANCE
    )?.id
    const isConditionalTimeFrame =
        data.pmt_type?.id === 1 || data.pmt_type?.title === PAYMENT_TYPE.SET_CONDITIONAL_TIME_FRAME
    const isOtherCondition = data.condition?.id === otherConditionId
    const isAlreadyPaidAsAdvance = data.condition?.id === 7 || data.condition?.id === alreadyPaidAsAdvanceId
    const shouldHideConditionalTimeFields = isConditionalTimeFrame && isAlreadyPaidAsAdvance
    const isAmountAllocation = data.allocation_type?.id === PAYMENT_ALLOCATION_TYPE.AMOUNT
    const paymentAllocationField = isAmountAllocation ? "pmt_amount" : "pmt_percent"
    const paymentAllocationValue = data[paymentAllocationField] || ""
    const hasManualOriginalAmtOverride = useRef(false)
    const lastAutoFilledOriginalAmt = useRef("")

    const getCalculatedOriginalAmount = () => {
        if (isAmountAllocation) {
            const paymentAmount = parseFloat(String(data.pmt_amount || 0))

            if (!Number.isFinite(paymentAmount) || paymentAmount <= 0) {
                return ""
            }

            return Number.isInteger(paymentAmount) ? String(paymentAmount) : String(Number(paymentAmount.toFixed(2)))
        }

        const totalAmount = parseFloat(String(formik.values.total_amount || 0))
        const paymentPercent = parseFloat(String(data.pmt_percent || 0))

        if (!Number.isFinite(totalAmount) || !Number.isFinite(paymentPercent) || paymentPercent <= 0) {
            return ""
        }

        const calculatedAmount = (totalAmount * paymentPercent) / 100

        if (!Number.isFinite(calculatedAmount)) {
            return ""
        }

        return Number.isInteger(calculatedAmount)
            ? String(calculatedAmount)
            : String(Number(calculatedAmount.toFixed(2)))
    }

    useEffect(() => {
        if (!isAlreadyPaidAsAdvance) {
            hasManualOriginalAmtOverride.current = false
            lastAutoFilledOriginalAmt.current = ""
            return
        }

        if (hasManualOriginalAmtOverride.current) {
            return
        }

        if (data.original_amt && data.original_amt !== lastAutoFilledOriginalAmt.current) {
            hasManualOriginalAmtOverride.current = true
            return
        }

        const calculatedOriginalAmount = getCalculatedOriginalAmount()

        if (!calculatedOriginalAmount) {
            return
        }

        lastAutoFilledOriginalAmt.current = calculatedOriginalAmount

        if (data.original_amt === calculatedOriginalAmount) {
            return
        }

        formik.setFieldValue(`payment_terms_obj[${id}].original_amt`, calculatedOriginalAmount)
    }, [
        data.allocation_type?.id,
        data.original_amt,
        data.pmt_amount,
        data.pmt_percent,
        formik.values.total_amount,
        formik,
        id,
        isAlreadyPaidAsAdvance,
        isAmountAllocation,
    ])

    const handleOriginalAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        const calculatedOriginalAmount = getCalculatedOriginalAmount()

        hasManualOriginalAmtOverride.current = value !== calculatedOriginalAmount
        formik.setFieldValue(`payment_terms_obj[${id}].original_amt`, value)
    }

    const handleAllocationTypeChange = (value: any) => {
        const nextType = value || PAYMENT_ALLOCATION_OPTIONS[0]

        formik.setFieldValue(`payment_terms_obj[${id}].allocation_type`, nextType)

        if (nextType.id === PAYMENT_ALLOCATION_TYPE.AMOUNT) {
            formik.setFieldValue(`payment_terms_obj[${id}].pmt_percent`, "")
            return
        }

        formik.setFieldValue(`payment_terms_obj[${id}].pmt_amount`, "")
    }

    const handlePaymentAllocationValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        formik.setFieldValue(`payment_terms_obj[${id}].${paymentAllocationField}`, e.target.value)
    }

    const handleEvidenceUpload = async (files: FileList) => {
        const file = files?.[0]
        if (!file) return
        if (!financeTicketId) return

        const formData = new FormData()
        formData.append("evidence_file", file)
        formData.append("finance_ticket_id", String(financeTicketId))

        const response = await uploadPaymentEvidence(formData).unwrap()
        const docId = response?.doc_id || response?.data?.doc_id || ""

        formik.setFieldValue(`payment_terms_obj[${id}].evidence_file_id`, docId)
    }

    return (
        <Box
            sx={{
                border: "1px solid #E0E0E0",
                borderRadius: "8px",
                padding: "16px",
                marginBottom: "16px",
            }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography fontWeight={500} fontSize="12px" color="statiqOrange.main">
                    PAYMENT TERM {count}
                    {showError && !data.pmt_type?.title && (
                        <FontAwesomeIcon icon={faCircleExclamation} color="#F94054" className="ml-4" />
                    )}
                </Typography>
                {count !== 1 && (
                    <Typography onClick={removeHandler} sx={{ color: "#E53935", cursor: "pointer", fontSize: "12px" }}>
                        Remove
                    </Typography>
                )}
            </Box>

            <Box display="flex" alignItems="center" gap={1} mb={2}>
                <RadioGroup
                    options={PAYMENT_TYPE_OPTIONS}
                    onSelect={(value: any) => {
                        formik.setFieldValue(`payment_terms_obj[${id}].pmt_type`, value)
                    }}
                    value={data.pmt_type}
                />
            </Box>

            {data.pmt_type?.title && (
                <>
                    {data.pmt_type?.title === PAYMENT_TYPE.SET_CONDITIONAL_TIME_FRAME ? (
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={2}>
                            <Box width="60px">
                                <MuiTextField
                                    type="number"
                                    placeholder="0"
                                    size="large"
                                    value={paymentAllocationValue}
                                    onChange={handlePaymentAllocationValueChange}
                                    error={showError && !paymentAllocationValue}
                                />
                            </Box>
                            <Box width="100px">
                                <MuiAutoComplete
                                    disablePortal={false}
                                    disableClearable
                                    options={
                                        allowAmountAllocation
                                            ? PAYMENT_ALLOCATION_OPTIONS
                                            : [PAYMENT_ALLOCATION_OPTIONS[0]]
                                    }
                                    disabled={!allowAmountAllocation}
                                    placeholder="Select"
                                    size="large"
                                    value={data.allocation_type || PAYMENT_ALLOCATION_OPTIONS[0]}
                                    onChange={handleAllocationTypeChange}
                                />
                            </Box>
                            {!shouldHideConditionalTimeFields ? (
                                <>
                                    <Typography fontSize="12px">
                                        {isAmountAllocation ? "to be paid within" : "of total to be paid within"}
                                    </Typography>
                                    <Box width="60px">
                                        <MuiTextField
                                            type="number"
                                            placeholder="0"
                                            size="large"
                                            value={data.time_qty}
                                            onChange={e =>
                                                formik.setFieldValue(
                                                    `payment_terms_obj[${id}].time_qty`,
                                                    e.target.value
                                                )
                                            }
                                            error={showError && !data.time_qty}
                                        />
                                    </Box>
                                    <Box width="100px">
                                        <MuiAutoComplete
                                            disablePortal={false}
                                            disableClearable
                                            options={TIME_UOM_OPTIONS}
                                            placeholder="Days"
                                            size="large"
                                            value={data.time_uom}
                                            onChange={(value: any) =>
                                                formik.setFieldValue(`payment_terms_obj[${id}].time_uom`, value)
                                            }
                                        />
                                    </Box>
                                </>
                            ) : !isAmountAllocation ? (
                                <Typography fontSize="12px">of total</Typography>
                            ) : null}
                            <Box width="180px">
                                <MuiAutoComplete
                                    disablePortal={false}
                                    disableClearable
                                    options={conditionOptions}
                                    placeholder="Select Condition"
                                    size="large"
                                    value={data.condition}
                                    onChange={(value: any) =>
                                        formik.setFieldValue(`payment_terms_obj[${id}].condition`, value)
                                    }
                                />
                            </Box>
                        </Box>
                    ) : (
                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                            <Box width="60px">
                                <MuiTextField
                                    type="number"
                                    placeholder="0"
                                    size="large"
                                    value={paymentAllocationValue}
                                    onChange={handlePaymentAllocationValueChange}
                                    error={showError && !paymentAllocationValue}
                                />
                            </Box>
                            <Box width="100px">
                                <MuiAutoComplete
                                    disablePortal={false}
                                    disableClearable
                                    options={
                                        allowAmountAllocation
                                            ? PAYMENT_ALLOCATION_OPTIONS
                                            : [PAYMENT_ALLOCATION_OPTIONS[0]]
                                    }
                                    disabled={!allowAmountAllocation}
                                    placeholder="Select"
                                    size="large"
                                    value={data.allocation_type || PAYMENT_ALLOCATION_OPTIONS[0]}
                                    onChange={handleAllocationTypeChange}
                                />
                            </Box>
                            <Typography fontSize="12px">
                                {isAmountAllocation ? "to be paid by" : "of total to be paid by"}
                            </Typography>
                            <Box width="150px">
                                <InputTag
                                    placeholder="Enter Date"
                                    type="date"
                                    useCustomDate={true}
                                    value={data.pmt_due_date}
                                    name="pmt_due_date"
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                        formik.setFieldValue(`payment_terms_obj[${id}].pmt_due_date`, e.target.value)
                                    }
                                    height="36px"
                                    error={showError && !data.pmt_due_date}
                                    showError={showError}
                                />
                            </Box>
                        </Box>
                    )}

                    {isConditionalTimeFrame && isOtherCondition && (
                        <Box mb={2}>
                            <MuiTextField
                                placeholder="Enter Payment Term*"
                                size="large"
                                fullWidth
                                value={data.condition_text}
                                onChange={e =>
                                    formik.setFieldValue(`payment_terms_obj[${id}].condition_text`, e.target.value)
                                }
                                error={showError && !data.condition_text}
                            />
                        </Box>
                    )}

                    {isConditionalTimeFrame && isAlreadyPaidAsAdvance && (
                        <Box display="flex" flexDirection="column" gap={2} mb={2}>
                            <Box display="flex" gap={2} flexWrap="wrap">
                                <Box flex={1} minWidth="150px">
                                    <MuiTextField
                                        placeholder="UTR Number*"
                                        size="large"
                                        fullWidth
                                        value={data.utr}
                                        onChange={e =>
                                            formik.setFieldValue(`payment_terms_obj[${id}].utr`, e.target.value)
                                        }
                                        error={showError && !data.utr}
                                    />
                                </Box>
                                <Box flex={1} minWidth="150px">
                                    <MuiTextField
                                        type="number"
                                        placeholder="Paid Amount*"
                                        size="large"
                                        fullWidth
                                        value={data.original_amt}
                                        onChange={handleOriginalAmountChange}
                                        error={showError && !data.original_amt}
                                    />
                                </Box>
                                <Box flex={1} minWidth="150px">
                                    <InputTag
                                        placeholder="Due Date of Payment*"
                                        type="date"
                                        useCustomDate={true}
                                        value={data.txn_due_date}
                                        name="txn_due_date"
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                            formik.setFieldValue(
                                                `payment_terms_obj[${id}].txn_due_date`,
                                                e.target.value
                                            )
                                        }
                                        height="44px"
                                        error={showError && !data.txn_due_date}
                                        showError={showError}
                                    />
                                </Box>
                                <Box flex={1} minWidth="150px">
                                    <InputTag
                                        placeholder="Actual Date of Payment*"
                                        type="date"
                                        useCustomDate={true}
                                        value={data.paid_at}
                                        name="paid_at"
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                            formik.setFieldValue(`payment_terms_obj[${id}].paid_at`, e.target.value)
                                        }
                                        height="44px"
                                        error={showError && !data.paid_at}
                                        showError={showError}
                                    />
                                </Box>
                            </Box>
                            <Box display="flex" gap={2} flexWrap="wrap">
                                <Box flex={1} minWidth="150px">
                                    <MuiTextField
                                        type="number"
                                        placeholder="Net Deduction*"
                                        size="large"
                                        fullWidth
                                        value={data.net_deduction}
                                        onChange={e =>
                                            formik.setFieldValue(
                                                `payment_terms_obj[${id}].net_deduction`,
                                                e.target.value
                                            )
                                        }
                                        error={showError && !data.net_deduction}
                                    />
                                </Box>
                                <Box flex={1} minWidth="150px">
                                    <MuiTextField
                                        placeholder="Remarks"
                                        size="large"
                                        fullWidth
                                        value={data.remarks}
                                        onChange={e =>
                                            formik.setFieldValue(`payment_terms_obj[${id}].remarks`, e.target.value)
                                        }
                                    />
                                </Box>
                                {showEvidenceUpload && (
                                    <Box flex={1} minWidth="150px">
                                        <FileUploaderV2
                                            onChange={handleEvidenceUpload}
                                            initialFileCount={data.evidence_file_id ? 1 : 0}
                                        />
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    )}
                </>
            )}

            <Box
                display="flex"
                alignItems="center"
                gap={1}
                sx={{ cursor: "pointer" }}
                onClick={() => {
                    formik.setFieldValue(`payment_terms_obj[${id}].bank_guarantee`, !data.bank_guarantee)
                }}>
                <MuiCheckbox
                    checked={data.bank_guarantee}
                    sx={{
                        mr: 0,
                    }}
                />
                <Typography variant="caption" fontSize="12px">
                    via collection of Bank Guarantee
                </Typography>
            </Box>
        </Box>
    )
}

interface PaymentTermsProps {
    formik: FormikProps<PaymentDetailsFormValues>
    financeTicketId?: number | null
    showEvidenceUpload?: boolean
    allowAmountAllocation?: boolean
}

const PaymentTerms = ({
    formik,
    financeTicketId,
    showEvidenceUpload = true,
    allowAmountAllocation = true,
}: PaymentTermsProps) => {
    const error = !isPaymentTermsTotalValid(formik.values.payment_terms_obj || [], formik.values.total_amount)

    return (
        <FormikProvider value={formik}>
            <Box
                sx={{
                    border: error && formik.submitCount > 0 ? "1px solid #E53935" : "1px solid #E0E0E0",
                    borderRadius: "8px",
                    padding: "16px",
                    marginTop: "16px",
                }}>
                <FieldArray
                    name="payment_terms_obj"
                    render={arrayHelpers => (
                        <>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                <Typography fontWeight={500} fontSize="14px">
                                    Payment Terms
                                </Typography>
                                <Typography
                                    onClick={() => arrayHelpers.push(PAYMENT_TERMS_INITIAL_VALUES)}
                                    className="text-decoration-link text-12">
                                    + Add new
                                </Typography>
                            </Box>
                            {formik.values.payment_terms_obj?.map((item: any, index: number) => (
                                <PaymentTerm
                                    key={index}
                                    formik={formik}
                                    financeTicketId={financeTicketId}
                                    showEvidenceUpload={showEvidenceUpload}
                                    allowAmountAllocation={allowAmountAllocation}
                                    count={index + 1}
                                    data={item}
                                    removeHandler={() => arrayHelpers.remove(index)}
                                    id={index}
                                />
                            ))}
                        </>
                    )}
                />
            </Box>
            {error && formik.submitCount > 0 && (
                <Typography color="error" className="text-12" mt={-2}>
                    Please make sure payment terms add up correctly: all % terms must total 100%, mixed % and amount
                    terms must equal the total amount, and all amount terms must total the total amount.
                </Typography>
            )}
        </FormikProvider>
    )
}

export default PaymentTerms
